import type { StyleAccum } from '../types/style-accum'
import type { ParagraphItem } from '../types/paragraph-item'
import { childStyleAccum } from './foreign-css-style'

export const BLOCK_TAGS = new Set([
  'P',
  'DIV',
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
  'LI',
  'BLOCKQUOTE',
  'PRE',
  'TR',
  'FIGURE',
])

export const SKIP_TAGS = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT', 'HEAD'])

/**
 * Walks the DOM, appending text/soft-break items to the last (current) paragraph in `paragraphs`.
 * Block-level elements (BLOCK_TAGS) seal a real paragraph boundary by pushing a fresh empty
 * paragraph once their subtree is done — unless that subtree already left one (e.g. a nested
 * block already sealed it), in which case it's reused so a wrapper `<div>` around a `<p>` doesn't
 * introduce a spurious extra paragraph. `<br>` stays a soft break *within* the current paragraph.
 */
export function walkNode(node: Node, state: StyleAccum, paragraphs: ParagraphItem[][]): void {
  if (node.nodeType === Node.TEXT_NODE) {
    const text = node.textContent ?? ''

    // Skip text nodes that are purely line-break whitespace (HTML formatting noise between elements).
    // Preserve space-only nodes (" ") since they may separate inline elements like <b>word</b> text.
    if (!text || (text.trim() === '' && text.includes('\n'))) return
    paragraphs[paragraphs.length - 1]!.push({ text, style: state })
    return
  }

  if (node.nodeType !== Node.ELEMENT_NODE) return

  const el = node as Element
  const tag = el.tagName.toUpperCase()

  if (SKIP_TAGS.has(tag)) return

  if (el instanceof HTMLElement) {
    const s = el.style

    if (s.display === 'none' || s.visibility === 'hidden') return
  }

  if (tag === 'BR') {
    paragraphs[paragraphs.length - 1]!.push({ softBreak: true })
    return
  }

  const isBlock = BLOCK_TAGS.has(tag)
  const openIndex = paragraphs.length - 1
  const childState = childStyleAccum(el, state)

  for (const child of Array.from(el.childNodes)) {
    walkNode(child, childState, paragraphs)
  }

  if (isBlock) {
    const closeIndex = paragraphs.length - 1

    if (closeIndex === openIndex) paragraphs.push([])
  }
}
